import { getTimezone } from '../data/timezones';
import { formatTimeOnly, zonedWallTimeToUtcDate } from './dateTimeUtils';
import { getIANATimezone, getTimezoneAbbreviation } from './timezoneUtils';

const COUNTRY_ALIASES = {
  'United States': 'USA',
  'United States of America': 'USA',
  'United Kingdom': 'UK',
  'Great Britain': 'UK',
};

const DAYLIGHT_IANA = {
  EDT: 'America/New_York',
  CDT: 'America/Chicago',
  MDT: 'America/Denver',
  PDT: 'America/Los_Angeles',
};

function resolveIana(timeZone) {
  if (!timeZone) return null;
  const raw = String(timeZone).trim();
  if (!raw) return null;
  const daylight = DAYLIGHT_IANA[raw.toUpperCase()];
  if (daylight) return daylight;
  return getIANATimezone(raw) || (raw.includes('/') || raw === 'UTC' ? raw : null);
}

function productZoneLabel(stored, iana) {
  const fromStored = stored ? getTimezoneAbbreviation(stored) : '';
  if (fromStored && !String(fromStored).includes('/')) return fromStored;
  const fromIana = iana ? getTimezoneAbbreviation(iana) : '';
  if (fromIana && !String(fromIana).includes('/')) return fromIana;
  return fromStored || fromIana || 'UTC';
}

/** Letter abbreviations from Intl (EDT, PDT). Offset labels (GMT+5:30) fall back to IST/GST/etc. */
function zoneLabel(utcDate, iana, stored) {
  const fallback = productZoneLabel(stored, iana);
  if (!utcDate || !iana) return fallback;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: iana,
      timeZoneName: 'short',
    }).formatToParts(utcDate);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    if (/^[A-Z]{2,5}$/.test(name)) return name;
  } catch {
    /* use the product abbreviation */
  }
  return fallback;
}

function clockParts(utcDate, iana) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(utcDate);
  const get = (type) => parts.find((part) => part.type === type)?.value || '';
  const hourNum = Number(get('hour'));
  const hour = Number.isNaN(hourNum) ? get('hour') : String(hourNum);
  const minute = String(get('minute') || '0').padStart(2, '0');
  const period = String(get('dayPeriod') || '').toUpperCase();
  const month = String(get('month') || '').padStart(2, '0');
  const day = String(get('day') || '').padStart(2, '0');
  return {
    time: `${hour}:${minute} ${period}`.trim(),
    date: `${get('year')}-${month}-${day}`,
  };
}

function rangeLine(startParts, endParts, label) {
  if (!endParts) return `${startParts.time} ${label} • ${startParts.date}`;
  if (startParts.date === endParts.date) {
    return `${startParts.time} – ${endParts.time} ${label} • ${startParts.date}`;
  }
  return `${startParts.time} ${label} • ${startParts.date} – ${endParts.time} ${label} • ${endParts.date}`;
}

function resolveStudentZone(student) {
  const raw = String(student?.timezone || '').trim();
  if (raw) return raw;
  const country = String(student?.country || '').trim();
  if (!country) return null;
  const tz = getTimezone(COUNTRY_ALIASES[country] || country);
  if (!tz || tz === 'UTC') return null;
  return tz;
}

/**
 * Teacher clock (stored session time) plus one line per distinct assigned-student timezone.
 * Student zones that match the teacher zone are omitted.
 */
export function formatSessionClockLines({
  sessionDate,
  startTime,
  endTime,
  teacherTimezone,
  students,
}) {
  const date = String(sessionDate || '').slice(0, 10);
  const teacherIana = resolveIana(teacherTimezone) || 'UTC';
  const startUtc = date && startTime ? zonedWallTimeToUtcDate(date, startTime, teacherIana) : null;
  const endUtc = date && endTime ? zonedWallTimeToUtcDate(date, endTime, teacherIana) : null;
  const teacherLabel = zoneLabel(startUtc, teacherIana, teacherTimezone || teacherIana);
  const startLabel = formatTimeOnly(startTime);
  const endLabel = formatTimeOnly(endTime);

  let teacherLine = '';
  if (startLabel && endLabel) {
    teacherLine = `${startLabel} – ${endLabel} ${teacherLabel}${date ? ` • ${date}` : ''}`;
  } else if (startLabel) {
    teacherLine = `${startLabel} ${teacherLabel}${date ? ` • ${date}` : ''}`;
  } else if (date) {
    teacherLine = date;
  }

  const seen = new Set();
  const studentLines = [];
  for (const student of students || []) {
    const stored = resolveStudentZone(student);
    if (!stored) continue;
    const iana = resolveIana(stored);
    if (!iana || iana === teacherIana || seen.has(iana)) continue;
    seen.add(iana);
    if (!startUtc) continue;
    const startParts = clockParts(startUtc, iana);
    const endParts = endUtc ? clockParts(endUtc, iana) : null;
    const label = zoneLabel(startUtc, iana, stored);
    studentLines.push({
      timezone: label,
      line: rangeLine(startParts, endParts, label),
    });
  }

  return {
    teacherLine,
    studentLines,
    studentLine: studentLines.map((row) => row.line).join(', '),
  };
}
